import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { CalendarIcon, Settings, ChevronRight, Check, RotateCcw, Loader2, Wand2 } from 'lucide-react';

const JOURS_CONFIG = [
  { key: 'lundi', label: 'Lundi' },
  { key: 'mardi', label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi', label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
  { key: 'samedi', label: 'Samedi' },
  { key: 'dimanche', label: 'Dimanche' },
];

export function PlanningAssistant({ api, semaine, setSemaine, fetchData, fetchSynthese, getCurrentWeek }) {
  const [config, setConfig] = useState(null);
  const [recap, setRecap] = useState([]);
  const [generated, setGenerated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [genWeek, setGenWeek] = useState(semaine || getCurrentWeek());
  const [selectedStaff, setSelectedStaff] = useState(new Set());
  const [customHours, setCustomHours] = useState({});
  const [expandedEmp, setExpandedEmp] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/planning-config');
      setConfig(res.data);
    } catch { toast.error("Erreur chargement config"); }
  }, [api]);

  const fetchRecap = useCallback(async () => {
    if (!genWeek) return;
    try {
      const res = await api.get('/planning-assistant/recap', { params: { semaine: genWeek } });
      setRecap(res.data);
    } catch { toast.error("Erreur chargement recap"); }
  }, [api, genWeek]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchConfig(), fetchRecap()]).finally(() => setLoading(false));
  }, [fetchConfig, fetchRecap]);

  useEffect(() => { fetchRecap(); }, [genWeek, fetchRecap]);

  useEffect(() => {
    if (recap.length > 0 && selectedStaff.size === 0) {
      setSelectedStaff(new Set(recap.map(e => e.prenom)));
    }
  }, [recap]);

  const toggleStaff = (prenom) => {
    setSelectedStaff(prev => {
      const next = new Set(prev);
      if (next.has(prenom)) next.delete(prenom); else next.add(prenom);
      return next;
    });
  };

  const toggleAllStaff = () => {
    if (selectedStaff.size === recap.length) setSelectedStaff(new Set());
    else setSelectedStaff(new Set(recap.map(e => e.prenom)));
  };

  const updateCustomHour = (prenom, field, value) => {
    setCustomHours(prev => ({ ...prev, [prenom]: { ...(prev[prenom] || {}), [field]: value } }));
  };

  const hasCustomHours = (prenom) => {
    const h = customHours[prenom];
    return h && (h.debut_midi || h.fin_midi || h.debut_soir || h.fin_soir);
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await api.put('/planning-config', config);
      setConfig(res.data);
      toast.success("Configuration sauvegardee");
    } catch { toast.error("Erreur"); }
    finally { setSavingConfig(false); }
  };

  const updateHoraire = (jour, service, field, value) => {
    setConfig(prev => ({
      ...prev,
      horaires: { ...prev.horaires, [jour]: { ...prev.horaires[jour], [service]: { ...prev.horaires[jour][service], [field]: value } } }
    }));
  };

  const toggleJour = (jour) => {
    setConfig(prev => ({
      ...prev,
      horaires: { ...prev.horaires, [jour]: { ...prev.horaires[jour], ouvert: !prev.horaires[jour].ouvert } }
    }));
  };

  const handleGenerate = async () => {
    if (selectedStaff.size === 0) { toast.error("Selectionnez au moins un employe"); return; }
    setGenerating(true);
    try {
      const res = await api.post('/planning-assistant/generate', {
        semaine: genWeek, annee: new Date().getFullYear(),
        selected_employees: Array.from(selectedStaff), custom_hours: customHours,
      });
      setGenerated(res.data);
      if (res.data.planning?.length === 0) toast.info("Aucun creneau supplementaire a generer");
      else toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur de generation"); }
    finally { setGenerating(false); }
  };

  const handleApply = async () => {
    if (!generated?.planning?.length) return;
    setApplying(true);
    try {
      const res = await api.post('/planning-assistant/apply', { planning: generated.planning });
      toast.success(res.data.message);
      setGenerated(null);
      fetchData(); fetchSynthese(); fetchRecap();
    } catch { toast.error("Erreur"); }
    finally { setApplying(false); }
  };

  if (loading || !config) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  const totalContrat = recap.reduce((s, e) => s + e.heures_contrat, 0);
  const totalPlanifie = recap.reduce((s, e) => s + e.heures_planifiees, 0);
  const totalRestant = recap.reduce((s, e) => s + e.heures_restantes, 0);

  return (
    <div className="space-y-6" data-testid="planning-assistant">
      <div className="flex items-center gap-4">
        <Select value={genWeek?.toString() || ''} onValueChange={(v) => setGenWeek(parseInt(v))}>
          <SelectTrigger className="w-[220px]" data-testid="assistant-week-select">
            <CalendarIcon className="w-4 h-4 mr-2" /><SelectValue placeholder="Choisir une semaine" />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 53 }, (_, i) => i + 1).map(s => (
              <SelectItem key={s} value={s.toString()}>
                {s === getCurrentWeek() ? `Semaine ${s} (actuelle)` : `Semaine ${s}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => setShowConfig(!showConfig)} data-testid="toggle-config-btn">
          <Settings className="w-4 h-4 mr-2" /> Configuration
          <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showConfig ? 'rotate-90' : ''}`} />
        </Button>
      </div>

      {showConfig && (
        <Card data-testid="config-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Horaires d'ouverture & besoins</CardTitle>
            <CardDescription>Configurez les horaires et le nombre d'employes par service</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {JOURS_CONFIG.map(({ key, label }) => {
                const jour = config.horaires[key];
                return (
                  <div key={key} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${jour.ouvert ? 'bg-green-50/50' : 'bg-muted/30'}`}
                    data-testid={`config-jour-${key}`}>
                    <div className="w-24 flex items-center gap-2">
                      <Switch checked={jour.ouvert} onCheckedChange={() => toggleJour(key)} data-testid={`switch-${key}`} />
                      <span className={`text-sm font-medium ${jour.ouvert ? '' : 'text-muted-foreground'}`}>{label}</span>
                    </div>
                    {jour.ouvert && (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground w-10">Midi</span>
                          <Input type="time" className="w-28 h-8 text-sm" value={jour.midi.debut}
                            onChange={e => updateHoraire(key, 'midi', 'debut', e.target.value)} />
                          <span className="text-muted-foreground">-</span>
                          <Input type="time" className="w-28 h-8 text-sm" value={jour.midi.fin}
                            onChange={e => updateHoraire(key, 'midi', 'fin', e.target.value)} />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground w-10">Soir</span>
                          <Input type="time" className="w-28 h-8 text-sm" value={jour.soir.debut}
                            onChange={e => updateHoraire(key, 'soir', 'debut', e.target.value)} />
                          <span className="text-muted-foreground">-</span>
                          <Input type="time" className="w-28 h-8 text-sm" value={jour.soir.fin}
                            onChange={e => updateHoraire(key, 'soir', 'fin', e.target.value)} />
                        </div>
                      </>
                    )}
                    {!jour.ouvert && <span className="text-sm text-muted-foreground italic">Ferme</span>}
                  </div>
                );
              })}
            </div>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Besoins par jour (journee complete midi + soir)</h4>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Titulaires (CDI)</Label>
                  <Input type="number" min={0} max={10} className="w-20 h-8" value={config.besoins?.titulaires ?? 1}
                    onChange={e => setConfig(prev => ({ ...prev, besoins: { ...prev.besoins, titulaires: parseInt(e.target.value) || 0 } }))}
                    data-testid="besoin-tit" />
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Alternants</Label>
                  <Input type="number" min={0} max={10} className="w-20 h-8" value={config.besoins?.alternants ?? 1}
                    onChange={e => setConfig(prev => ({ ...prev, besoins: { ...prev.besoins, alternants: parseInt(e.target.value) || 0 } }))}
                    data-testid="besoin-alt" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-sm">Jours de repos / semaine / employe</Label>
              <Input type="number" min={0} max={7} className="w-20 h-8" value={config.jours_repos_semaine}
                onChange={e => setConfig(prev => ({ ...prev, jours_repos_semaine: parseInt(e.target.value) || 1 }))}
                data-testid="jours-repos" />
            </div>
            <div className="flex justify-end">
              <Button onClick={saveConfig} disabled={savingConfig} data-testid="save-config-btn">
                {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Sauvegarder la configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="recap-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Staff & heures — Semaine {genWeek}</CardTitle>
              <CardDescription>Cochez les employes a inclure, cliquez sur un employe pour pre-remplir ses horaires</CardDescription>
            </div>
            <Badge variant="outline" className="text-sm">{selectedStaff.size}/{recap.length} selectionnes</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input type="checkbox" className="rounded" checked={selectedStaff.size === recap.length && recap.length > 0}
                    onChange={toggleAllStaff} data-testid="select-all-staff" />
                </TableHead>
                <TableHead>Employe</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Contrat</TableHead>
                <TableHead className="text-right">Planifie</TableHead>
                <TableHead className="text-right">Restant</TableHead>
                <TableHead className="w-[100px]">Jauge</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recap.map(emp => {
                const pct = emp.heures_contrat > 0 ? Math.min(100, (emp.heures_planifiees / emp.heures_contrat) * 100) : 0;
                const isOver = emp.heures_restantes < 0;
                const isSelected = selectedStaff.has(emp.prenom);
                const isExpanded = expandedEmp === emp.prenom;
                const hasCustom = hasCustomHours(emp.prenom);
                return (
                  <React.Fragment key={emp.prenom}>
                    <TableRow className={`${isSelected ? '' : 'opacity-40'} cursor-pointer hover:bg-muted/30`}
                      data-testid={`recap-row-${emp.prenom}`}>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <input type="checkbox" className="rounded" checked={isSelected}
                          onChange={() => toggleStaff(emp.prenom)} data-testid={`select-${emp.prenom}`} />
                      </TableCell>
                      <TableCell onClick={() => isSelected && setExpandedEmp(isExpanded ? null : emp.prenom)}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: emp.couleur }} />
                          <span className="font-medium">{emp.prenom}</span>
                          {hasCustom && <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">Horaires perso</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={emp.is_titulaire ? 'default' : 'secondary'} className="text-xs">
                          {emp.is_titulaire ? 'CDI' : 'Alternant'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{emp.heures_contrat}h</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{emp.heures_planifiees}h</TableCell>
                      <TableCell className={`text-right tabular-nums font-semibold ${isOver ? 'text-red-600' : emp.heures_restantes <= 5 ? 'text-amber-600' : 'text-green-600'}`}>
                        {emp.heures_restantes}h
                      </TableCell>
                      <TableCell>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {isSelected && (
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => setExpandedEmp(isExpanded ? null : emp.prenom)}
                            data-testid={`expand-${emp.prenom}`}>
                            <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && isSelected && (
                      <TableRow className="bg-blue-50/30">
                        <TableCell colSpan={8} className="py-3">
                          <div className="pl-8 space-y-2">
                            <p className="text-xs text-muted-foreground font-medium">
                              Horaires personnalises pour {emp.prenom} — appliques a tous ses jours de travail. Laissez vide pour utiliser les horaires par defaut.
                            </p>
                            <div className="flex items-center gap-3" data-testid={`custom-hours-${emp.prenom}`}>
                              <span className="text-sm font-medium w-12">Midi</span>
                              <Input type="time" className="w-28 h-8 text-sm" value={customHours[emp.prenom]?.debut_midi || ''}
                                onChange={e => updateCustomHour(emp.prenom, 'debut_midi', e.target.value)} />
                              <span className="text-muted-foreground">-</span>
                              <Input type="time" className="w-28 h-8 text-sm" value={customHours[emp.prenom]?.fin_midi || ''}
                                onChange={e => updateCustomHour(emp.prenom, 'fin_midi', e.target.value)} />
                              <span className="text-sm font-medium w-12 ml-4">Soir</span>
                              <Input type="time" className="w-28 h-8 text-sm" value={customHours[emp.prenom]?.debut_soir || ''}
                                onChange={e => updateCustomHour(emp.prenom, 'debut_soir', e.target.value)} />
                              <span className="text-muted-foreground">-</span>
                              <Input type="time" className="w-28 h-8 text-sm" value={customHours[emp.prenom]?.fin_soir || ''}
                                onChange={e => updateCustomHour(emp.prenom, 'fin_soir', e.target.value)} />
                              {hasCustom && (
                                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground"
                                  onClick={() => setCustomHours(prev => { const n = { ...prev }; delete n[emp.prenom]; return n; })}>
                                  Reinitialiser
                                </Button>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {recap.length > 0 && (
                <TableRow className="font-semibold bg-muted/30">
                  <TableCell></TableCell>
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell className="text-right tabular-nums">{totalContrat}h</TableCell>
                  <TableCell className="text-right tabular-nums">{totalPlanifie}h</TableCell>
                  <TableCell className={`text-right tabular-nums ${totalRestant < 0 ? 'text-red-600' : 'text-green-600'}`}>{totalRestant}h</TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card data-testid="generator-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><Wand2 className="w-5 h-5" /> Generateur automatique</CardTitle>
          <CardDescription>
            Genere un planning pour la semaine {genWeek} en respectant les contraintes (heures contrat, jours de repos, besoins en titulaires/alternants)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={handleGenerate} disabled={generating || !genWeek} className="gap-2" data-testid="generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            Generer le planning — Semaine {genWeek}
          </Button>

          {generated && generated.planning?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-green-700">{generated.planning.length} creneaux proposes</h4>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setGenerated(null)} data-testid="cancel-generated-btn">
                    <RotateCcw className="w-4 h-4 mr-1" /> Annuler
                  </Button>
                  <Button size="sm" onClick={handleApply} disabled={applying} data-testid="apply-generated-btn">
                    {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    Appliquer au planning
                  </Button>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Employe</TableHead>
                      <TableHead className="text-center">Midi</TableHead>
                      <TableHead className="text-center">Soir</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {generated.planning.map((entry, idx) => (
                      <TableRow key={idx} className="bg-green-50/30" data-testid={`generated-row-${idx}`}>
                        <TableCell className="text-sm">{entry.date}</TableCell>
                        <TableCell className="font-medium">{entry.employe}</TableCell>
                        <TableCell className="text-center text-sm">
                          {entry.debut_midi && entry.fin_midi ? `${entry.debut_midi} - ${entry.fin_midi}` : '-'}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {entry.debut_soir && entry.fin_soir ? `${entry.debut_soir} - ${entry.fin_soir}` : '-'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{entry.total?.toFixed(1)}h</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
